import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Bot, CalendarClock, CircleAlert, MessageCircle, Star, UserPlus } from "lucide-react";
import config from "../../config";
import { PageLoader } from "./PageLoader";

/**
 * The bell and its panel (design: components/NotificationsBell.tsx), fed by
 * the real notifications API (/api/notifications).
 *
 * The Support tab is the "support" category (complaints and feedback, item 2);
 * priority "urgent" shows the red icon (item 3).
 */

// Design tab → API category
export const NOTE_TABS = ["All", "WhatsApp", "CRM", "AI Agent", "Support"];
const TAB_CATEGORY = { All: "", WhatsApp: "waba", CRM: "crm", "AI Agent": "ai_agent", Support: "support" };
const CATEGORY_KIND = { waba: "WhatsApp", crm: "CRM", ai_agent: "AI Agent", support: "Support", billing: "Wallet", marketing: "Marketing" };

// link_type → panel route (same mapping as the old bell).
const LINK_ROUTES = {
  conversation: (id) => `/waba/conversations/${id}`,
  lead: (id) => `/crm/leads/${id}`,
  appointment: (id) => `/appointments/${id}`,
  complaint: (id) => `/support/complaints?open=${id}`,
  feedback: (id) => `/support/feedback?open=${id}`,
  ai_conversation: () => "/waba/conversations?tab=ai_agent",
};
const LINK_ICON = { conversation: MessageCircle, lead: UserPlus, appointment: CalendarClock, complaint: CircleAlert, feedback: Star, ai_conversation: Bot };
const CATEGORY_ICON = { waba: MessageCircle, crm: UserPlus, ai_agent: Bot, support: CircleAlert };

export const KIND_TONE = { WhatsApp: "bg-success-soft text-success", CRM: "bg-sky-soft text-sky-deep", Appointments: "bg-sky-soft text-sky-deep", "AI Agent": "bg-sun-soft text-ink", Support: "bg-warning-soft text-warning", Marketing: "bg-mist text-ink", Wallet: "bg-mist text-ink" };

const MIN = 60_000;
const HOUR = 60 * MIN;
/** The design's relative time ("Just now", "5 min ago", "3 h ago", "Yesterday", "4 days ago"). */
export const timeAgo = (at, now = Date.now()) => {
  const diff = Math.max(0, now - at);
  if (diff < MIN) return "Just now";
  if (diff < HOUR) return `${Math.floor(diff / MIN)} min ago`;
  if (diff < 24 * HOUR) return `${Math.floor(diff / HOUR)} h ago`;
  const days = Math.floor(diff / (24 * HOUR));
  return days === 1 ? "Yesterday" : `${days} days ago`;
};

const POLL_MS = 30000;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

/** API row → the design's note shape. */
const toNote = (n) => {
  const route = LINK_ROUTES[n.link_type];
  return {
    id: n.id,
    kind: CATEGORY_KIND[n.category] || "CRM",
    title: n.title,
    detail: n.body || "",
    at: new Date(n.created_at).getTime(),
    href: route ? route(n.link_id) : null,
    icon: LINK_ICON[n.link_type] || CATEGORY_ICON[n.category] || Bell,
    read: !!n.is_read,
    urgent: n.priority === "urgent",
  };
};

export function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("All");
  const [notes, setNotes] = useState([]);
  // The tab the list was loaded for: another tab shows the loader, not this tab's
  // rows; a late answer for a tab no longer open is dropped.
  const [notesTab, setNotesTab] = useState(null);
  const latestTab = useRef(tab);
  latestTab.current = tab;
  const notesTabRef = useRef(null);
  const [counts, setCounts] = useState({ total: 0, byCategory: { waba: 0, crm: 0, ai_agent: 0 } });
  const wrap = useRef(null);
  const panel = useRef(null);
  const [pos, setPos] = useState({ top: 72, right: 16 });
  const place = () => { const el = wrap.current; if (!el) return; const b = el.getBoundingClientRect(); setPos({ top: Math.floor(b.bottom + 8), right: Math.max(16, Math.floor(window.innerWidth - b.right)) }); };
  useEffect(() => { if (!open) return undefined; place(); window.addEventListener("resize", place); window.addEventListener("scroll", place, true); return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); }; }, [open]);

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/api/notifications/unread-count`, auth());
      if (data.success) setCounts({ total: data.total, byCategory: data.byCategory || {} });
    } catch (_) { /* a stale badge is better than an error on every page */ }
  }, []);

  const fetchNotes = useCallback(async (t) => {
    const category = TAB_CATEGORY[t];
    try {
      const params = new URLSearchParams({ limit: "20" });
      if (category) params.append("category", category);
      const { data } = await axios.get(`${config.API_BASE_URL}/api/notifications?${params}`, auth());
      if (latestTab.current !== t) return;
      if (data.success) setNotes((data.notifications || []).map(toNote));
      else if (notesTabRef.current !== t) setNotes([]); // not another tab's rows under this one
    } catch (_) {
      if (latestTab.current !== t) return;
      setNotes([]);
    }
    notesTabRef.current = t;
    setNotesTab(t);
  }, []);

  useEffect(() => {
    fetchCounts();
    const t = setInterval(fetchCounts, POLL_MS);
    return () => clearInterval(t);
  }, [fetchCounts]);
  useEffect(() => { if (open) fetchNotes(tab); }, [open, tab, fetchNotes]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { const t = e.target; if (!wrap.current?.contains(t) && !panel.current?.contains(t)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [open]);

  const unreadTotal = counts.total || 0;
  const tabCount = (t) => (t === "All" ? unreadTotal : TAB_CATEGORY[t] ? counts.byCategory?.[TAB_CATEGORY[t]] || 0 : 0);
  const list = notes.slice(0, 8);

  const markRead = async (n) => {
    if (n.read) return;
    setNotes((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    try { await axios.patch(`${config.API_BASE_URL}/api/notifications/${n.id}/read`, {}, auth()); } catch (_) { /* refetch below corrects it */ }
    fetchCounts();
  };
  const openNote = (n) => {
    markRead(n);
    if (!n.href) return;
    setOpen(false);
    navigate(n.href);
  };
  const markAll = async () => {
    const category = TAB_CATEGORY[tab];
    try { await axios.post(`${config.API_BASE_URL}/api/notifications/read-all`, category ? { category } : {}, auth()); } catch (_) { /* counts refresh below */ }
    setNotes((prev) => prev.map((x) => ({ ...x, read: true })));
    fetchCounts();
  };
  const tabUnread = tabCount(tab);

  return (
    <div ref={wrap} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="dialog" aria-label={unreadTotal ? `Notifications, ${unreadTotal} unread` : "Notifications"} className={`relative flex h-11 w-11 items-center justify-center rounded-full bg-paper text-ink shadow-soft ring-1 ring-line hover:bg-mist ${open ? "ring-ink/30" : ""}`}>
        <Bell size={20} strokeWidth={1.75} />
        {unreadTotal > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-ink px-1 text-caption font-semibold tabular-nums text-paper ring-2 ring-mist">{unreadTotal > 9 ? "9+" : unreadTotal}</span>}
      </button>
      {open && createPortal(
        <section ref={panel} role="dialog" aria-label="Notifications" style={{ top: pos.top, right: pos.right }} className="olli animate-fade-in fixed z-[80] flex max-h-[min(560px,calc(100vh-96px))] w-[440px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-lg bg-paper text-ink shadow-lift ring-1 ring-line">
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-3">
            <div>
              <h2 className="text-body font-semibold">Notifications</h2>
              <p className="text-caption text-muted">{unreadTotal ? `${unreadTotal} unread` : "You're all caught up"}</p>
            </div>
            <button type="button" onClick={markAll} disabled={!tabUnread} className="rounded-full px-3 py-1.5 text-caption font-semibold text-sky-deep hover:bg-mist disabled:text-muted disabled:hover:bg-transparent">Mark all as read</button>
          </header>
          <div className="flex shrink-0 flex-wrap gap-1 px-4 pb-1 pt-3" role="tablist" aria-label="Filter notifications">
            {NOTE_TABS.map((t) => { const c = tabCount(t); return <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={`flex h-8 shrink-0 items-center gap-1 rounded-full px-2.5 text-caption font-semibold ${tab === t ? "bg-ink text-paper" : "bg-mist text-muted hover:text-ink"}`}>{t}{c > 0 && <span className={`tabular-nums ${tab === t ? "text-paper/70" : "text-muted"}`}>{c}</span>}</button>; })}
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            {notesTab !== tab ? <li><PageLoader label="Loading notifications…" className="py-10" /></li> : list.map((n) => {
              const Icon = n.icon;
              const isUnread = !n.read;
              return (
                <li key={n.id}>
                  <button type="button" onClick={() => openNote(n)} className="flex w-full items-start gap-3 rounded-md px-2 py-2.5 text-left hover:bg-mist">
                    <Icon size={18} strokeWidth={1.75} className={`mt-0.5 shrink-0 ${n.urgent ? "text-danger" : "text-muted"}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-small ${isUnread ? "font-semibold text-ink" : "text-ink"}`}>{n.title}</span>
                      <span className="mt-0.5 block truncate text-caption text-muted">{n.detail}</span>
                      <span className="mt-1.5 flex items-center gap-2"><span className={`rounded-xs px-1.5 py-0.5 text-caption font-semibold ${KIND_TONE[n.kind]}`}>{n.kind}</span><span className="text-caption text-muted">{timeAgo(n.at)}</span></span>
                    </span>
                    {isUnread && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-sky-deep" aria-label="Unread" />}
                  </button>
                </li>
              );
            })}
            {notesTab === tab && !list.length && <li className="px-4 py-10 text-center text-small text-muted">{tab === "All" ? "Nothing new right now." : `No ${tab} notifications.`}</li>}
          </ul>
          <footer className="shrink-0 border-t border-line p-2">
            <button type="button" onClick={() => { setOpen(false); navigate("/notifications"); }} className="flex h-10 w-full items-center justify-center rounded-md text-small font-semibold text-ink hover:bg-mist">
              View all notifications{notes.length > list.length ? ` (${notes.length})` : ""}
            </button>
          </footer>
        </section>,
        document.body,
      )}
    </div>
  );
}
